import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, Loader2, Lock, LogOut, Plus, Pencil, Trash2, Save, X, Upload,
  FileText, Users, Wallet, Car, Shield, ChevronRight, CheckCircle2, FileSignature, Printer,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Ethics & Professional Conduct',
  'Client Engagement Policy',
  'Audit & Assurance Policy',
  'Taxation & Compliance Policy',
  'Technology & Data Governance Policy',
  'Sustainability & CSR Assurance Policy',
  'Forensic & Risk Management Policy',
  'HR Policies',
];
const STATUSES = ['Draft', 'Active', 'Superseded'];
const MAX_REVISIONS = 4;

const STATUS_TONE = {
  'Draft':      'bg-amber-50 text-amber-700 border-amber-200',
  'Active':     'bg-green-50 text-green-700 border-green-200',
  'Superseded': 'bg-gray-100 text-gray-600 border-gray-200',
};

const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};
const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' · ');
  return String(detail);
};

const emptyPolicy = (category) => ({
  category,
  title: '',
  purpose: '',
  scope: '',
  date_of_adoption: '',
  definitions: '',
  policy_body: '',
  procedure: '',
  documents_and_flows: '',
  references: '',
  prepared_by: '',
  approved_by: '',
  version: '1.0',
  status: 'Draft',
  signed_document_path: null,
  revisions: [],
  sort_order: 100,
});

const FieldGroup = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const PoliciesPage = () => {
  const { user, ready, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [policies, setPolicies]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [activeCat, setActiveCat] = useState(CATEGORIES[0]);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [viewing, setViewing]     = useState(null);
  const [signedUploading, setSignedUploading] = useState(false);
  const [revUploading, setRevUploading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const sidebarTimer = useRef(null);
  const signedRef = useRef(null);
  const revRef    = useRef(null);

  const scheduleSidebarHide = () => {
    clearTimeout(sidebarTimer.current);
    sidebarTimer.current = setTimeout(() => setSidebarOpen(false), 2000);
  };
  const keepSidebarOpen = () => {
    setSidebarOpen(true);
    scheduleSidebarHide();
  };
  useEffect(() => {
    scheduleSidebarHide();
    return () => clearTimeout(sidebarTimer.current);
  }, []);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/policies', { replace: true });
  }, [ready, user, navigate]);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/policies`);
      setPolicies(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ready && user) load(); /* eslint-disable-next-line */ }, [ready, user]);

  const countsByCat = useMemo(() => {
    const map = new Map(CATEGORIES.map((c) => [c, 0]));
    policies.forEach((p) => map.set(p.category, (map.get(p.category) || 0) + 1));
    return map;
  }, [policies]);

  const visible = useMemo(
    () => policies.filter((p) => p.category === activeCat).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [policies, activeCat]
  );

  const openNew  = () => { setEditing(emptyPolicy(activeCat)); setError(''); };
  const openEdit = (p) => {
    setEditing({
      ...emptyPolicy(p.category),
      ...p,
      revisions: Array.isArray(p.revisions) ? p.revisions : [],
    });
    setError('');
  };
  const cancel   = () => { setEditing(null); setError(''); };
  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));

  const uploadSigned = async (file) => {
    if (!file) return;
    setSignedUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/policies/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEditing((e) => ({ ...e, signed_document_path: data.path }));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Upload failed');
    } finally { setSignedUploading(false); }
  };

  const uploadRevision = async (file) => {
    if (!file) return;
    if ((editing.revisions || []).length >= MAX_REVISIONS) {
      setError(`Maximum ${MAX_REVISIONS} revisions allowed. Delete an existing revision first.`);
      return;
    }
    setRevUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/admin/policies/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Auto-suggest next version number (1.1, 1.2, ...) based on current base
      const base = (editing.version || '1.0').split('.')[0];
      const usedMinors = (editing.revisions || [])
        .map((r) => r.version)
        .filter((v) => v && v.startsWith(`${base}.`))
        .map((v) => Number((v.split('.')[1] || '0')))
        .filter((n) => !isNaN(n));
      const nextMinor = (usedMinors.length ? Math.max(...usedMinors) : 0) + 1;
      const suggestedVersion = `${base}.${nextMinor}`;
      setEditing((e) => ({
        ...e,
        revisions: [
          ...(e.revisions || []),
          { version: suggestedVersion, path: data.path, notes: '', uploaded_at: new Date().toISOString() },
        ],
      }));
      // Reset file input so the same file can be picked again if needed
      if (revRef.current) revRef.current.value = '';
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Upload failed');
    } finally { setRevUploading(false); }
  };

  const updateRevision = (idx, key, value) =>
    setEditing((e) => ({
      ...e,
      revisions: e.revisions.map((r, i) => i === idx ? { ...r, [key]: value } : r),
    }));

  const removeRevision = (idx) =>
    setEditing((e) => ({ ...e, revisions: e.revisions.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!editing.category) { setError('Category is required'); return; }
    if (!editing.title?.trim()) { setError('Title is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        category: editing.category,
        title: editing.title.trim(),
        purpose: editing.purpose || null,
        scope: editing.scope || null,
        date_of_adoption: editing.date_of_adoption || null,
        definitions: editing.definitions || null,
        policy_body: editing.policy_body || null,
        procedure: editing.procedure || null,
        documents_and_flows: editing.documents_and_flows || null,
        references: editing.references || null,
        prepared_by: (editing.prepared_by || '').trim() || null,
        approved_by: (editing.approved_by || '').trim() || null,
        version: (editing.version || '1.0').trim(),
        status: editing.status || 'Draft',
        signed_document_path: editing.signed_document_path || null,
        revisions: (editing.revisions || []).slice(0, MAX_REVISIONS).map((r) => ({
          version: (r.version || '').trim(),
          path: r.path,
          notes: r.notes || null,
        })).filter((r) => r.version && r.path),
        sort_order: Number(editing.sort_order) || 100,
      };
      if (editing.id) await axios.put(`${API}/admin/policies/${editing.id}`, payload);
      else            await axios.post(`${API}/admin/policies`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/policies/${p.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  if (!ready || !user) {
    return <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center"><Loader2 className="text-brand-yellow animate-spin" size={22} /></div>;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="policies-page">
      {/* Admin bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[3%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Lock size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">Admin Dashboard</div>
              <div className="text-sm font-medium">Policies &amp; SOP · {policies.length} documents</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={openNew} className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold" data-testid="add-policy-btn">
              <Plus size={16} className="mr-1.5" /> New Policy
            </Button>
            <button onClick={() => navigate('/team')}           className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Users size={13} /> Team</button>
            <button onClick={() => navigate('/admin/cashflow')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Wallet size={13} /> Cashflow</button>
            <button onClick={() => navigate('/admin/vehicles')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Car size={13} /> Vehicles</button>
            <button onClick={() => navigate('/admin/payroll')}  className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><span className="font-mono">₹</span> Payroll</button>
            <button onClick={() => navigate('/admin/insurances')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2"><Shield size={13} /> Insurances</button>
            {isAdmin && (
              <button onClick={() => navigate('/admin/firm-documents')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2" title="Confidential Firm Documents" data-testid="nav-firm-docs-btn"><FileText size={13} /> Firm Docs</button>
            )}
            {isAdmin && (
              <button onClick={() => navigate('/admin/access')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2" title="User & Access Control" data-testid="nav-access-btn"><Lock size={13} /> Access</button>
            )}
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">{user.email}</span>
            <button onClick={async () => { await logout(); navigate('/'); }} className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"><LogOut size={13} /> Log out</button>
          </div>
        </div>
      </div>

      {/* Hero (compact) */}
      <section className="relative bg-[#0a0f1e] pt-8 pb-8 overflow-hidden print:hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-[3%]">
          <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-3">
            <BookOpen size={13} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">Internal · R S T &amp; Co.</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-white leading-[1.05] tracking-tight">
            Policies &amp; SOP Manual
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-2xl">
            Firm policies with versioned, signed documents. Each category holds one or more policies with up to {MAX_REVISIONS} dated revisions.
          </p>
        </div>
      </section>

      {/* Body: sidebar + content — stretch to bottom of viewport */}
      <div className="flex-1 max-w-[1720px] w-full mx-auto px-[3%] pt-6 pb-6 flex gap-6 items-stretch">
        {/* Sidebar — auto-hides after 2s of inactivity, reopens on hover */}
        {sidebarOpen ? (
          <aside
            className="w-64 shrink-0 border border-gray-200 flex flex-col transition-all duration-300"
            data-testid="policies-sidebar"
            onMouseEnter={keepSidebarOpen}
            onMouseMove={keepSidebarOpen}
            onMouseLeave={scheduleSidebarHide}
          >
            <div className="bg-gray-900 text-white px-4 py-3 font-mono text-[10px] tracking-widest uppercase sticky top-[68px] z-10">
              Categories
            </div>
            <nav className="flex flex-col flex-1">
              {CATEGORIES.map((cat) => {
                const active = activeCat === cat;
                const count  = countsByCat.get(cat) || 0;
                return (
                  <button
                    key={cat}
                    onClick={() => setActiveCat(cat)}
                    className={`text-left flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 text-sm transition-colors ${
                      active
                        ? 'bg-brand-yellow/10 text-gray-900 font-semibold border-l-4 border-l-brand-yellow'
                        : 'hover:bg-gray-50 text-gray-700 border-l-4 border-l-transparent'
                    }`}
                    data-testid={`policy-cat-${CATEGORIES.indexOf(cat)}`}
                  >
                    <span className="leading-tight">{cat}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 border ${active ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>
        ) : (
          <div
            className="w-9 shrink-0 border border-gray-200 bg-gray-900 text-white flex flex-col items-center gap-3 py-3 cursor-pointer hover:bg-black transition-colors sticky top-[68px] self-start"
            data-testid="policies-sidebar-collapsed"
            onMouseEnter={keepSidebarOpen}
            title="Show categories"
          >
            <ChevronRight size={16} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-widest uppercase" style={{ writingMode: 'vertical-rl' }}>
              Categories · {policies.length}
            </span>
          </div>
        )}

        {/* Content */}
        <main className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
            <div>
              <div className="font-mono text-[10px] tracking-widest text-brand-blue uppercase mb-1">
                {String(CATEGORIES.indexOf(activeCat) + 1).padStart(2, '0')} of {CATEGORIES.length}
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-black text-gray-900">{activeCat}</h2>
            </div>
            <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-in-category-btn">
              <Plus size={14} className="mr-1.5" /> Add policy here
            </Button>
          </div>

          {error && !editing && (
            <div className="border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-2 mb-4">{error}</div>
          )}

          {loading ? (
            <div className="flex-1 py-16 text-center text-gray-500 flex flex-col items-center justify-center">
              <Loader2 className="mb-3 animate-spin" size={22} />
              Loading policies…
            </div>
          ) : visible.length === 0 ? (
            <div className="flex-1 border-2 border-dashed border-gray-200 py-16 text-center flex flex-col items-center justify-center">
              <BookOpen size={32} className="text-gray-300 mb-3" />
              <p className="text-gray-500 text-sm mb-4">No policy under this category yet.</p>
              <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-first-here-btn">
                <Plus size={14} className="mr-1.5" /> Create first policy
              </Button>
            </div>
          ) : activeCat === 'HR Policies' ? (
            <div className="flex-1 flex flex-col gap-2" data-testid="hr-policy-dropdown">
              {visible.map((p) => (
                <CollapsiblePolicyItem
                  key={p.id}
                  policy={p}
                  onView={setViewing}
                  onEdit={openEdit}
                  onDelete={remove}
                />
              ))}
            </div>
          ) : (
            <div className="flex-1 flex flex-col gap-4">
              {visible.map((p) => (
                <PolicyCard
                  key={p.id}
                  policy={p}
                  onView={setViewing}
                  onEdit={openEdit}
                  onDelete={remove}
                  stretch={visible.length === 1}
                />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end print:hidden" onClick={cancel} data-testid="policy-editor">
          <div className="w-full max-w-4xl bg-white h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[9px] tracking-widest text-brand-yellow uppercase">
                  {editing.id ? 'Edit policy' : 'New policy'} · {editing.category}
                </div>
                <div className="font-heading text-xl font-bold text-gray-900">{editing.title || 'Untitled policy'}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button onClick={save} disabled={saving} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="save-policy-btn">
                  {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save</>}
                </Button>
                <button onClick={cancel} className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" title="Close">
                  <X size={16} />
                </button>
              </div>
            </div>

            {error && <div className="mx-6 mt-4 border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>}

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldGroup label="Category" className="md:col-span-2">
                  <select value={editing.category} onChange={(e) => handleField('category', e.target.value)} className="admin-input" data-testid="editor-category">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Status">
                  <select value={editing.status} onChange={(e) => handleField('status', e.target.value)} className="admin-input" data-testid="editor-status">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </FieldGroup>
              </div>

              <FieldGroup label="Title*">
                <input value={editing.title} onChange={(e) => handleField('title', e.target.value)} className="admin-input" placeholder="e.g. Code of Professional Conduct" data-testid="editor-title" />
              </FieldGroup>

              <FieldGroup label="Purpose">
                <textarea value={editing.purpose || ''} onChange={(e) => handleField('purpose', e.target.value)} className="admin-input min-h-[80px]" placeholder="Why does this policy exist? What outcomes does it target?" data-testid="editor-purpose" />
              </FieldGroup>

              <FieldGroup label="Scope">
                <textarea value={editing.scope || ''} onChange={(e) => handleField('scope', e.target.value)} className="admin-input min-h-[68px]" placeholder="Who does it apply to? Any exclusions?" data-testid="editor-scope" />
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Date of Adoption">
                  <input type="date" value={toISODate(editing.date_of_adoption)} onChange={(e) => handleField('date_of_adoption', fromISODate(e.target.value))} className="admin-input" data-testid="editor-adoption-date" />
                </FieldGroup>
                <FieldGroup label="Version">
                  <input value={editing.version || '1.0'} onChange={(e) => handleField('version', e.target.value)} className="admin-input font-mono" placeholder="1.0" data-testid="editor-version" />
                </FieldGroup>
              </div>

              <FieldGroup label="Definitions">
                <textarea value={editing.definitions || ''} onChange={(e) => handleField('definitions', e.target.value)} className="admin-input min-h-[80px]" placeholder="Key terms used in this policy…" data-testid="editor-definitions" />
              </FieldGroup>

              <FieldGroup label="Policy">
                <textarea value={editing.policy_body || ''} onChange={(e) => handleField('policy_body', e.target.value)} className="admin-input min-h-[160px]" placeholder="The full text of the policy…" data-testid="editor-policy-body" />
              </FieldGroup>

              <FieldGroup label="Procedure & Workflow Steps">
                <textarea value={editing.procedure || ''} onChange={(e) => handleField('procedure', e.target.value)} className="admin-input min-h-[140px]" placeholder="Step-by-step procedure. Numbered lists work well here." data-testid="editor-procedure" />
              </FieldGroup>

              <FieldGroup label="Documents and Flows">
                <textarea
                  value={editing.documents_and_flows || ''}
                  onChange={(e) => handleField('documents_and_flows', e.target.value)}
                  className="admin-input min-h-[140px]"
                  placeholder="Templates, forms, checklists and workflow diagrams referenced by this policy. Numbered lists work here too."
                  data-testid="editor-documents-and-flows"
                />
              </FieldGroup>

              <FieldGroup label="References">
                <textarea value={editing.references || ''} onChange={(e) => handleField('references', e.target.value)} className="admin-input min-h-[68px]" placeholder="ICAI, Companies Act, Income-tax Act, standards…" data-testid="editor-references" />
              </FieldGroup>

              {/* Signed document */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3 flex items-center gap-2">
                  <FileSignature size={12} /> Signed Master Document
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 border border-gray-300 bg-gray-50 flex items-center justify-center overflow-hidden text-center px-2">
                    {editing.signed_document_path ? (
                      <a href={`${API}/media/${editing.signed_document_path}`} target="_blank" rel="noreferrer" className="text-xs text-brand-blue hover:underline">
                        <FileText size={20} className="mx-auto mb-1 text-gray-600" />
                        View / Download
                      </a>
                    ) : <FileText size={22} className="text-gray-400" />}
                  </div>
                  <div className="flex-1">
                    <input ref={signedRef} type="file" accept="application/pdf,image/*,.doc,.docx" className="hidden" onChange={(e) => uploadSigned(e.target.files?.[0])} />
                    <Button type="button" onClick={() => signedRef.current?.click()} disabled={signedUploading} className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9" data-testid="upload-signed-btn">
                      {signedUploading
                        ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                        : <><Upload size={14} className="mr-2" /> {editing.signed_document_path ? 'Replace' : 'Upload'} signed document</>}
                    </Button>
                    <p className="text-[11px] text-gray-500 mt-1">PDF / DOC / Image · up to 25 MB.</p>
                  </div>
                </div>
              </div>

              {/* Revisions */}
              <div className="border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase">
                    Revisions ({(editing.revisions || []).length} / {MAX_REVISIONS})
                  </div>
                  <div>
                    <input ref={revRef} type="file" accept="application/pdf,image/*,.doc,.docx" className="hidden" onChange={(e) => uploadRevision(e.target.files?.[0])} />
                    <Button
                      type="button"
                      onClick={() => revRef.current?.click()}
                      disabled={revUploading || (editing.revisions || []).length >= MAX_REVISIONS}
                      className="rounded-none bg-gray-900 hover:bg-black text-white text-xs h-9 disabled:opacity-60"
                      data-testid="upload-revision-btn"
                    >
                      {revUploading
                        ? <><Loader2 size={14} className="mr-2 animate-spin" /> Uploading…</>
                        : <><Upload size={14} className="mr-2" /> Upload revision</>}
                    </Button>
                  </div>
                </div>
                {(editing.revisions || []).length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No revisions yet. Up to {MAX_REVISIONS} versioned revision documents can be attached.</p>
                ) : (
                  <div className="divide-y divide-gray-200 border border-gray-200">
                    <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-gray-50 text-[10px] font-mono tracking-widest text-gray-500 uppercase">
                      <div className="col-span-2">Version</div>
                      <div className="col-span-4">Notes</div>
                      <div className="col-span-3">File</div>
                      <div className="col-span-2">Uploaded</div>
                      <div className="col-span-1"></div>
                    </div>
                    {editing.revisions.map((r, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`revision-row-${idx}`}>
                        <input
                          value={r.version || ''}
                          onChange={(e) => updateRevision(idx, 'version', e.target.value)}
                          placeholder="1.1"
                          className="col-span-2 border border-gray-300 px-2 py-1 text-xs font-mono focus:outline-none focus:border-gray-900"
                        />
                        <input
                          value={r.notes || ''}
                          onChange={(e) => updateRevision(idx, 'notes', e.target.value)}
                          placeholder="What changed in this revision?"
                          className="col-span-4 border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-gray-900"
                        />
                        <div className="col-span-3">
                          <a href={`${API}/media/${r.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-blue hover:underline">
                            <FileText size={12} /> View
                          </a>
                        </div>
                        <div className="col-span-2 text-[11px] text-gray-500">
                          {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString('en-IN') : '—'}
                        </div>
                        <button onClick={() => removeRevision(idx)} className="col-span-1 justify-self-end p-1 text-red-500 hover:bg-red-50" title="Remove">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sign-off */}
              <div className="border border-gray-900 bg-gray-900 text-white p-5">
                <div className="font-mono text-[10px] tracking-widest text-brand-yellow uppercase mb-3">Sign-off</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FieldGroup label="Prepared by">
                    <input value={editing.prepared_by || ''} onChange={(e) => handleField('prepared_by', e.target.value)} className="admin-input" placeholder="Name of the drafter" data-testid="editor-prepared-by" />
                  </FieldGroup>
                  <FieldGroup label="Approved by">
                    <input value={editing.approved_by || ''} onChange={(e) => handleField('approved_by', e.target.value)} className="admin-input" placeholder="Approving partner / authority" data-testid="editor-approved-by" />
                  </FieldGroup>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Policy reader — beautiful full-view */}
      {viewing && (
        <PolicyReader
          policy={viewing}
          onClose={() => setViewing(null)}
          onEdit={() => { openEdit(viewing); setViewing(null); }}
          onPrint={() => window.print()}
        />
      )}

      <style>{`
        .admin-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          font-size: 14px;
          outline: none;
          background: white;
          color: #111827;
        }
        .admin-input:focus { border-color: #111827; }
      `}</style>
    </div>
  );
};

const CollapsiblePolicyItem = ({ policy, onView, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 bg-white" data-testid={`hr-policy-item-${policy.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-gray-50 transition-colors"
        data-testid={`hr-policy-toggle-${policy.id}`}
        aria-expanded={open}
      >
        <ChevronRight
          size={16}
          className={`text-brand-yellow shrink-0 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
        />
        <span className="font-heading font-bold text-gray-900 flex-1 min-w-0 truncate">{policy.title}</span>
        {policy.version && (
          <span className="font-mono text-[10px] text-gray-500 shrink-0">v{policy.version}</span>
        )}
        <span className={`text-[10px] font-mono px-1.5 py-0.5 border shrink-0 ${policy.status === 'Active' ? 'border-green-300 text-green-700 bg-green-50' : 'border-gray-300 text-gray-500'}`}>
          {policy.status || 'Draft'}
        </span>
      </button>
      {open && (
        <div className="border-t border-gray-100" data-testid={`hr-policy-body-${policy.id}`}>
          <PolicyCard policy={policy} onView={onView} onEdit={onEdit} onDelete={onDelete} />
        </div>
      )}
    </div>
  );
};

const PolicyCard = ({ policy, onView, onEdit, onDelete, stretch = false }) => {
  const revCount = (policy.revisions || []).length;
  return (
    <div className={`border border-gray-200 hover:border-gray-900 transition-colors bg-white group flex flex-col ${stretch ? 'flex-1' : ''}`} data-testid={`policy-card-${policy.id}`}>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-4 mb-3">
          <button
            onClick={() => onView(policy)}
            className="text-left flex-1 min-w-0"
            data-testid={`view-policy-${policy.id}`}
            title="Open full policy"
          >
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className={`inline-block text-[10px] px-2 py-0.5 border ${STATUS_TONE[policy.status] || STATUS_TONE.Draft}`}>{policy.status}</span>
              <span className="text-[10px] font-mono text-gray-500">v{policy.version}</span>
              {policy.date_of_adoption && <span className="text-[10px] font-mono text-gray-500">· Adopted {policy.date_of_adoption}</span>}
              {revCount > 0 && <span className="text-[10px] font-mono text-gray-500">· {revCount} revision{revCount > 1 ? 's' : ''}</span>}
            </div>
            <h3 className="font-heading text-lg md:text-xl font-black text-gray-900 leading-tight group-hover:underline decoration-brand-yellow decoration-4 underline-offset-4">
              {policy.title}
            </h3>
          </button>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onView(policy)}   className="p-2 border border-gray-900 bg-gray-900 text-white hover:bg-brand-yellow hover:text-gray-900 hover:border-brand-yellow transition-colors text-xs font-semibold inline-flex items-center gap-1" title="Read full policy" data-testid={`read-policy-${policy.id}`}>
              <BookOpen size={13} /> Read
            </button>
            <button onClick={() => onEdit(policy)}   className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" title="Edit" data-testid={`edit-policy-${policy.id}`}>
              <Pencil size={13} />
            </button>
            <button onClick={() => onDelete(policy)} className="p-2 border border-red-100 text-red-600 hover:border-red-500 hover:bg-red-50" title="Delete" data-testid={`delete-policy-${policy.id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        </div>

        {(policy.purpose || policy.scope) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700 mb-3">
            {policy.purpose && (
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-1 h-3.5 bg-brand-yellow shrink-0" />
                  <span className="font-mono text-[11px] tracking-[0.18em] text-gray-900 uppercase font-bold">Purpose</span>
                </div>
                <p className="whitespace-pre-wrap line-clamp-3 leading-[1.5]">{policy.purpose}</p>
              </div>
            )}
            {policy.scope && (
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-1 h-3.5 bg-brand-yellow shrink-0" />
                  <span className="font-mono text-[11px] tracking-[0.18em] text-gray-900 uppercase font-bold">Scope</span>
                </div>
                <p className="whitespace-pre-wrap line-clamp-3 leading-[1.5]">{policy.scope}</p>
              </div>
            )}
          </div>
        )}

        {(() => {
          const extra = [
            { key: 'definitions',         label: 'Definitions' },
            { key: 'policy_body',         label: 'Policy' },
            { key: 'procedure',           label: 'Procedure & Workflow Steps' },
            { key: 'documents_and_flows', label: 'Documents and Flows' },
            { key: 'references',          label: 'References' },
          ].filter((s) => (policy[s.key] || '').trim());
          if (!extra.length) return null;
          return (
            <div className="flex-1 min-h-0 overflow-y-auto border-t border-gray-100 pt-4 mb-3 columns-1 md:columns-2 gap-8" data-testid={`policy-card-extra-${policy.id}`}>
              {extra.map((s) => (
                <div key={s.key} className="break-inside-avoid mb-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-1 h-4 bg-brand-yellow shrink-0" />
                    <span className="font-mono text-[11px] tracking-[0.18em] text-gray-900 uppercase font-bold">{s.label}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-[1.5]">{policy[s.key]}</p>
                </div>
              ))}
            </div>
          );
        })()}

        <div className="flex flex-wrap items-center gap-4 pt-3 mt-auto border-t border-gray-100 text-xs">
          {policy.signed_document_path && (
            <a href={`${API}/media/${policy.signed_document_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-blue hover:underline font-medium">
              <FileSignature size={14} /> Signed document
            </a>
          )}
          {revCount > 0 && (
            <div className="inline-flex items-center gap-1.5 text-gray-500">
              <ChevronRight size={14} />
              {policy.revisions.slice(0, MAX_REVISIONS).map((r) => (
                <a key={r.version + r.path} href={`${API}/media/${r.path}`} target="_blank" rel="noreferrer" className="text-brand-blue hover:underline font-mono">
                  v{r.version}
                </a>
              )).reduce((acc, el, i, arr) => i === 0 ? [el] : [...acc, <span key={`sep-${i}`} className="text-gray-300">|</span>, el], [])}
            </div>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-3 text-[11px] text-gray-500 font-mono">
            {policy.prepared_by && <span><span className="text-gray-400">Prepared:</span> {policy.prepared_by}</span>}
            {policy.approved_by && <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-green-600" /><span className="text-gray-400">Approved:</span> {policy.approved_by}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PolicyReader — beautiful full-page read view of a policy.
// Renders numbered sections in a legible document style with sign-off footer.
// ---------------------------------------------------------------------------
const READER_SECTIONS = [
  { key: 'purpose',             label: 'Purpose' },
  { key: 'scope',               label: 'Scope' },
  { key: 'definitions',         label: 'Definitions' },
  { key: 'policy_body',         label: 'Policy' },
  { key: 'procedure',           label: 'Procedure & Workflow Steps' },
  { key: 'documents_and_flows', label: 'Documents and Flows' },
  { key: 'references',          label: 'References' },
];

/**
 * Split a block of text into structured paragraphs & list items.
 * A line starting with "1.", "1)", "a.", "a)", "•", "-", "*" becomes a list item.
 * Consecutive plain lines merge into a single paragraph.
 * This gives the reader a clean, flowing look regardless of how the admin typed.
 */
const parseBlock = (text) => {
  if (!text) return [];
  const lines = String(text).split(/\r?\n/);
  const out = [];
  let para = [];
  const flushPara = () => {
    if (para.length) {
      out.push({ type: 'p', text: para.join(' ').trim() });
      para = [];
    }
  };
  const numRe   = /^\s*(\d+)[.)]\s+(.*)$/;
  const alphaRe = /^\s*([a-zA-Z])[.)]\s+(.*)$/;
  const bulletRe = /^\s*[•\-*]\s+(.*)$/;
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) { flushPara(); continue; }
    let m;
    if ((m = line.match(numRe))) {
      flushPara();
      out.push({ type: 'ol', marker: `${m[1]}.`, text: m[2] });
    } else if ((m = line.match(alphaRe))) {
      flushPara();
      out.push({ type: 'ol', marker: `${m[1].toLowerCase()}.`, text: m[2], nested: true });
    } else if ((m = line.match(bulletRe))) {
      flushPara();
      out.push({ type: 'ul', text: m[1] });
    } else {
      para.push(line);
    }
  }
  flushPara();
  return out;
};

const RenderedBlock = ({ text }) => {
  const items = parseBlock(text);
  if (!items.length) return <p className="text-gray-500 italic">—</p>;
  return (
    <div className="space-y-3 text-[15px] leading-[1.75] text-gray-800">
      {items.map((it, i) => {
        if (it.type === 'p') {
          return <p key={i} className="whitespace-pre-wrap">{it.text}</p>;
        }
        if (it.type === 'ol') {
          return (
            <div key={i} className={`flex gap-3 ${it.nested ? 'ml-8' : ''}`}>
              <span className="shrink-0 font-mono font-semibold text-brand-blue min-w-[1.75rem]">{it.marker}</span>
              <span className="flex-1">{it.text}</span>
            </div>
          );
        }
        // ul
        return (
          <div key={i} className="flex gap-3">
            <span className="shrink-0 text-brand-yellow font-bold mt-0.5">•</span>
            <span className="flex-1">{it.text}</span>
          </div>
        );
      })}
    </div>
  );
};

const PolicyReader = ({ policy, onClose, onEdit, onPrint }) => {
  const revCount = (policy.revisions || []).length;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-stretch justify-center print:relative print:bg-white print:py-0"
      onClick={onClose}
      data-testid="policy-reader"
    >
      <div
        className="w-full max-w-[1400px] bg-white shadow-2xl print:shadow-none print:max-w-none h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky action bar */}
        <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-gray-200 px-8 py-3 flex items-center justify-between z-10 print:hidden">
          <div className="flex items-center gap-2 text-[11px] font-mono tracking-widest text-gray-500 uppercase">
            <BookOpen size={13} className="text-brand-blue" /> Policy Reader
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onPrint} className="inline-flex items-center gap-1.5 border border-gray-300 hover:border-gray-900 hover:bg-gray-100 px-3 py-1.5 text-xs" data-testid="reader-print-btn">
              <Printer size={12} /> Print
            </button>
            <button onClick={onEdit} className="inline-flex items-center gap-1.5 border border-gray-300 hover:border-gray-900 hover:bg-gray-100 px-3 py-1.5 text-xs" data-testid="reader-edit-btn">
              <Pencil size={12} /> Edit
            </button>
            <button onClick={onClose} className="p-1.5 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" data-testid="reader-close-btn" title="Close">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Document */}
        <article className="px-8 md:px-14 py-10 md:py-14">
          {/* Header */}
          <div className="pb-6 mb-8 border-b-2 border-gray-900">
            <div className="font-mono text-[10px] tracking-[0.25em] text-brand-blue uppercase mb-3">
              {policy.category}
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-gray-900 leading-[1.05] tracking-tight mb-5">
              {policy.title}
            </h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-gray-600">
              <div>
                <span className="text-gray-400">Version</span>&nbsp;<b className="text-gray-900">v{policy.version}</b>
              </div>
              <div>
                <span className="text-gray-400">Status</span>&nbsp;<b className={`${policy.status === 'Active' ? 'text-green-700' : policy.status === 'Draft' ? 'text-amber-700' : 'text-gray-700'}`}>{policy.status}</b>
              </div>
              {policy.date_of_adoption && (
                <div>
                  <span className="text-gray-400">Date of Adoption</span>&nbsp;<b className="text-gray-900">{policy.date_of_adoption}</b>
                </div>
              )}
              {revCount > 0 && (
                <div>
                  <span className="text-gray-400">Revisions</span>&nbsp;<b className="text-gray-900">{revCount}</b>
                </div>
              )}
            </div>
          </div>

          {/* Numbered sections (skip empties but preserve visible numbering 1-6) */}
          <div className="space-y-10">
            {READER_SECTIONS.map((s, idx) => {
              const val = policy[s.key];
              if (!val || !String(val).trim()) return null;
              return (
                <section key={s.key} data-testid={`reader-section-${s.key}`}>
                  <div className="flex items-baseline gap-4 mb-4">
                    <div className="font-heading text-3xl font-black text-brand-yellow tabular-nums leading-none">
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <h2 className="font-heading text-xl md:text-2xl font-black text-gray-900 border-b border-gray-200 pb-1 flex-1">
                      {s.label}
                    </h2>
                  </div>
                  <div className="pl-14">
                    <RenderedBlock text={val} />
                  </div>
                </section>
              );
            })}
          </div>

          {/* Signed document + revisions attachments */}
          {(policy.signed_document_path || revCount > 0) && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-4">Attachments</div>
              <div className="flex flex-wrap gap-3">
                {policy.signed_document_path && (
                  <a href={`${API}/media/${policy.signed_document_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-gray-900 bg-gray-900 text-white hover:bg-black px-4 py-2 text-xs font-semibold">
                    <FileSignature size={14} /> Signed Master Document
                  </a>
                )}
                {(policy.revisions || []).map((r, i) => (
                  <a key={i} href={`${API}/media/${r.path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-gray-300 hover:border-gray-900 hover:bg-gray-100 px-4 py-2 text-xs">
                    <FileText size={14} />
                    <span className="font-mono">v{r.version}</span>
                    {r.notes && <span className="text-gray-500 max-w-[240px] truncate">· {r.notes}</span>}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Sign-off footer */}
          {(policy.prepared_by || policy.approved_by) && (
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="border-t-2 border-gray-900 pt-3">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-1">Prepared by</div>
                <div className="font-heading text-lg font-bold text-gray-900">{policy.prepared_by || '—'}</div>
              </div>
              <div className="border-t-2 border-gray-900 pt-3">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-1 inline-flex items-center gap-1.5">
                  <CheckCircle2 size={11} className="text-green-600" /> Approved by
                </div>
                <div className="font-heading text-lg font-bold text-gray-900">{policy.approved_by || '—'}</div>
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default PoliciesPage;